/**
 * Pure utilities and constants for the PB Stundenplan (US-005).
 * Extracted from PbScheduleForm so they can be unit-tested in isolation.
 */
import type { PbScheduleBlock } from '../../../../lib/api';

/** Generischer Tag — alle Blöcke werden unter diesem Wochentag gespeichert. */
export const SCHEDULE_WEEKDAY = 1;

/** Feste Pause (Minuten) und frei einplanbares Budget (6,6 h = 396 Min). */
export const FIXED_BREAK_MIN = 120;
export const DAY_ACTIVITY_BUDGET_MIN = 396; // 6,6 Std frei einplanbar

/** Tätigkeiten mit fixer Dauer — die Bis-Zeit wird automatisch gesetzt. */
export const PAUSE_ACTIVITY = 'Pause';
export const MEDICATION_ACTIVITY = 'Medikamenten-Gabe / Erinnerung';
export const MEDICATION_DURATION_MIN = 5;

/** Voreingestellte Pause für einen neuen (leeren) Tagesplan: 13:00–15:00. */
export const DEFAULT_PAUSE_START = '13:00';
export const DEFAULT_PAUSE_END = '15:00';

/** Pause-Block, mit dem ein leerer Tagesplan vorbelegt wird. */
export function defaultPauseBlock(weekday: number = SCHEDULE_WEEKDAY): PbScheduleBlock {
  return {
    weekday,
    start_time: DEFAULT_PAUSE_START,
    end_time: DEFAULT_PAUSE_END,
    care_type: PAUSE_ACTIVITY,
    note: null,
  };
}

/**
 * Tätigkeiten für das Dropdown (Quelle: Senihelp24-Tätigkeitsliste).
 * Wert == Anzeige; der Wert wird als `care_type` gespeichert.
 * `Pause` (fixe 2 h) ist auswählbar, zählt aber nicht zum 6,6-h-Budget.
 */
export const PB_ACTIVITIES: readonly string[] = [
  'Morgen-Hygiene / Aufstehen',
  'Abend-Hygiene / zu Bett bringen',
  'Große Körperhygiene (Duschen / Baden)',
  MEDICATION_ACTIVITY,
  'An- / Auskleiden / Hilfe',
  'Planbarer Toilettengang',
  'Frühstück/Abendessen vorbereiten',
  'Mahlzeit vorbereiten / Kochen',
  'Gemeinsam essen',
  'Mahlzeit Nachbereitung / aufräumen',
  'Spazieren gehen / Mobilisation',
  'Aktivieren / Spielen / Unterhaltung',
  'Hauswirtschaft',
  'Einkaufen',
  PAUSE_ACTIVITY,
] as const;

/**
 * i18n key per activity — the array above stays the stored `care_type` value
 * (German, persisted/sent to the backend); only the DISPLAYED label is
 * translated via this lookup (see i18n hardcoded-strings rollout).
 */
export const ACTIVITY_LABEL_KEYS: Readonly<Record<string, string>> = {
  'Morgen-Hygiene / Aufstehen': 'pflege.schedule.activity_options.morgenhygiene',
  'Abend-Hygiene / zu Bett bringen': 'pflege.schedule.activity_options.abendhygiene',
  'Große Körperhygiene (Duschen / Baden)': 'pflege.schedule.activity_options.grosse_koerperhygiene',
  [MEDICATION_ACTIVITY]: 'pflege.schedule.activity_options.medikamente',
  'An- / Auskleiden / Hilfe': 'pflege.schedule.activity_options.ankleiden',
  'Planbarer Toilettengang': 'pflege.schedule.activity_options.toilettengang',
  'Frühstück/Abendessen vorbereiten': 'pflege.schedule.activity_options.fruehstueck_abendessen',
  'Mahlzeit vorbereiten / Kochen': 'pflege.schedule.activity_options.mahlzeit_kochen',
  'Gemeinsam essen': 'pflege.schedule.activity_options.gemeinsam_essen',
  'Mahlzeit Nachbereitung / aufräumen': 'pflege.schedule.activity_options.mahlzeit_nachbereitung',
  'Spazieren gehen / Mobilisation': 'pflege.schedule.activity_options.spazieren',
  'Aktivieren / Spielen / Unterhaltung': 'pflege.schedule.activity_options.aktivieren',
  'Hauswirtschaft': 'pflege.schedule.activity_options.hauswirtschaft',
  'Einkaufen': 'pflege.schedule.activity_options.einkaufen',
  [PAUSE_ACTIVITY]: 'pflege.schedule.break',
};

/** Translated display label for a stored `care_type` value (falls back to the raw value). */
export function activityLabel(careType: string | null | undefined, tr: (key: string) => string): string {
  if (!careType) return '';
  const key = ACTIVITY_LABEL_KEYS[careType];
  return key ? tr(key) : careType;
}

/**
 * Standard-Dauer (Minuten) je Tätigkeit — Quelle: Senihelp24-Zeitliste.
 * Tätigkeiten mit FIXER Dauer (Pause 120, Medikamente 5) stehen hier NICHT.
 */
export const ACTIVITY_DEFAULT_MIN: Readonly<Record<string, number>> = {
  'Morgen-Hygiene / Aufstehen': 20,
  'Abend-Hygiene / zu Bett bringen': 20,
  'Große Körperhygiene (Duschen / Baden)': 45,
  'An- / Auskleiden / Hilfe': 20,
  'Frühstück/Abendessen vorbereiten': 15,
  'Mahlzeit vorbereiten / Kochen': 45,
  'Gemeinsam essen': 30,
  'Mahlzeit Nachbereitung / aufräumen': 30,
  'Spazieren gehen / Mobilisation': 60,
  'Aktivieren / Spielen / Unterhaltung': 30,
  'Hauswirtschaft': 60,
  'Einkaufen': 90,
};

/** Letzte Minute des Tages (23:59). */
export const DAY_END_MIN = 23 * 60 + 59;

export type ScheduleValidationError =
  | { code: 'INCOMPLETE' }
  | { code: 'NO_ACTIVITY' }
  | { code: 'END_BEFORE_START' }
  | { code: 'OVERLAP' }
  | { code: 'OVERLIMIT'; totalMinutes: number };

/**
 * Zeitfenster der Tagesübersicht: der ganze Tag, 00:00–24:00. Gespeichert
 * werden Einträge weiter höchstens bis 23:59 (`DAY_END_MIN`, das Backend
 * akzeptiert nur `H:i`); 24:00 ist nur die untere Rasterkante.
 */
export const DEFAULT_WINDOW_START_MIN = 0;
export const DEFAULT_WINDOW_END_MIN = 24 * 60;

/** Startposition des Rasters ohne Einträge (außer der voreingestellten Pause): 06:00. */
export const DEFAULT_SCROLL_MIN = 6 * 60;

/** Längste Beschreibung (Spalte `note` ist varchar(255), Backend-Regel `max:255`). */
export const NOTE_MAX_LEN = 255;

/**
 * Minute, bei der das Raster beim Öffnen steht: eine Stunde vor dem ersten
 * Eintrag (die Pause zählt nicht, sie ist voreingestellt), sonst 06:00.
 */
export function initialScrollMin(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'care_type'>>,
): number {
  const starts = blocks
    .filter(b => b.care_type !== PAUSE_ACTIVITY)
    .map(b => timeToMinutes(b.start_time))
    .filter(m => !Number.isNaN(m));
  if (starts.length === 0) return DEFAULT_SCROLL_MIN;
  return Math.max(0, Math.floor(Math.min(...starts) / 60) * 60 - 60);
}

/**
 * Pflicht zur Beschreibung: für neu angelegte Einträge immer (außer Pause, die
 * ist voreingestellt und braucht keinen Text). Bestehende Einträge ohne
 * Beschreibung bleiben speicherbar, solange sie leer bleiben.
 */
export function isNoteRequired(opts: {
  careType: string | null;
  isNew: boolean;
  originalNote: string | null | undefined;
}): boolean {
  if (opts.careType === PAUSE_ACTIVITY) return false;
  return opts.isNew || !!opts.originalNote?.trim();
}

export type EntryDraftError = 'NO_ACTIVITY' | 'INVALID_TIME' | 'OVERLAP' | 'NOTE_REQUIRED' | 'NOTE_TOO_LONG';

/**
 * Prüft einen Entwurf (Drawer) vor dem Übernehmen. `others` sind die übrigen
 * Einträge (ohne den bearbeiteten).
 */
export function validateEntryDraft(
  draft: { care_type: string | null; start_time: string; end_time: string; note: string | null },
  others: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>,
  noteRequired: boolean,
): EntryDraftError | null {
  if (!draft.care_type) return 'NO_ACTIVITY';
  const s = timeToMinutes(draft.start_time);
  const e = timeToMinutes(draft.end_time);
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return 'INVALID_TIME';
  if (rangeOverlaps(s, e, others)) return 'OVERLAP';
  const note = (draft.note ?? '').trim();
  if (noteRequired && note === '') return 'NOTE_REQUIRED';
  if (note.length > NOTE_MAX_LEN) return 'NOTE_TOO_LONG';
  return null;
}

/**
 * Index des ersten in dieser Sitzung neu angelegten Eintrags (`newKeys`) ohne
 * Beschreibung — Rückfall für das Speichern im CRM, falls sie inline wieder
 * geleert wurde. Bestehende Einträge und die Pause bleiben unberührt, damit
 * alte Pläne ohne Beschreibung weiter speicherbar sind.
 */
export function firstMissingNoteIndex(
  blocks: Array<Pick<PbScheduleBlock, 'care_type' | 'note'> & { _key: string }>,
  newKeys: ReadonlySet<string>,
): number {
  return blocks.findIndex(b => newKeys.has(b._key) && b.care_type !== PAUSE_ACTIVITY && !(b.note ?? '').trim());
}

/** Dauer für Tätigkeiten ohne Standard-Dauer (z. B. „Planbarer Toilettengang"). */
export const FALLBACK_ACTIVITY_MIN = 60;

/** Ein Slot der Tagesübersicht-Tabelle. */
export interface DaySlot {
  /** Startzeit des Slots in Minuten seit Mitternacht. */
  startMin: number;
  /** Anzeige-Label, z. B. "07:30". */
  label: string;
  /** Index des Blocks, der diesen Slot belegt — oder -1, wenn frei. */
  blockIndex: number;
}

/**
 * Eine absolut positionierte Block-Box der Tagesübersicht.
 */
export interface LayoutBox {
  /** Index des Blocks im ursprünglichen Array (für Render/Selektion). */
  blockIndex: number;
  /** Start in Minuten seit Mitternacht. */
  startMin: number;
  /** Ende in Minuten seit Mitternacht. */
  endMin: number;
  /** Vertikaler Offset in % der Fensterhöhe. */
  topPct: number;
  /** Höhe in % der Fensterhöhe (mind. so groß, dass die Box sichtbar bleibt). */
  heightPct: number;
}

export interface ScheduleLayout {
  windowStartMin: number;
  windowEndMin: number;
  /** Für JEDEN gültigen Block eine sichtbare Box. */
  boxes: LayoutBox[];
}

/** "HH:MM" → Minuten seit Mitternacht; NaN bei ungültiger Eingabe. */
export function timeToMinutes(t: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!m) return NaN;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Dauer eines Blocks in Minuten (0 bei ungültiger/unvollständiger Zeit). */
export function blockMinutes(b: Pick<PbScheduleBlock, 'start_time' | 'end_time'>): number {
  const s = timeToMinutes(b.start_time);
  const e = timeToMinutes(b.end_time);
  if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return 0;
  return e - s;
}

/** Summe der geplanten Tätigkeitszeiten in Minuten. */
export function totalPlannedMinutes(blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>): number {
  return blocks.reduce((acc, b) => acc + blockMinutes(b), 0);
}

/** Minuten seit Mitternacht → "HH:MM". */
export function fmtClock(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Baut das Zeitraster der Tagesübersicht: feste Slots (Default 30 Min) über
 * den Tag, jeweils mit dem belegenden Block (oder -1 = frei).
 */
export function buildDaySlots(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>,
  slotMin = 30,
): DaySlot[] {
  const valid = blocks
    .map(b => ({ s: timeToMinutes(b.start_time), e: timeToMinutes(b.end_time) }))
    .filter(r => !Number.isNaN(r.s) && !Number.isNaN(r.e) && r.e > r.s);

  let lo = DEFAULT_WINDOW_START_MIN;
  let hi = DEFAULT_WINDOW_END_MIN;
  for (const r of valid) {
    lo = Math.min(lo, Math.floor(r.s / 60) * 60);
    hi = Math.max(hi, Math.ceil(r.e / 60) * 60);
  }

  const indexed = blocks.map((b, i) => ({ i, s: timeToMinutes(b.start_time), e: timeToMinutes(b.end_time) }))
    .filter(r => !Number.isNaN(r.s) && !Number.isNaN(r.e) && r.e > r.s);

  const slots: DaySlot[] = [];
  for (let start = lo; start < hi; start += slotMin) {
    const end = start + slotMin;
    const hit = indexed.find(r => r.s < end && r.e > start);
    slots.push({ startMin: start, label: fmtClock(start), blockIndex: hit ? hit.i : -1 });
  }
  return slots;
}

/**
 * Berechnet das Zeitfenster der Tagesübersicht (in Minuten seit Mitternacht).
 */
export function dayWindow(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>,
): { startMin: number; endMin: number } {
  let lo = DEFAULT_WINDOW_START_MIN;
  let hi = DEFAULT_WINDOW_END_MIN;
  for (const b of blocks) {
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) continue;
    lo = Math.min(lo, Math.floor(s / 60) * 60);
    hi = Math.max(hi, Math.ceil(e / 60) * 60);
  }
  return { startMin: lo, endMin: hi };
}

/**
 * Baut das Layout der Tagesübersicht als absolut positionierte Boxen.
 */
export function layoutBlocks(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>,
  minVisiblePct = 2,
): ScheduleLayout {
  const { startMin, endMin } = dayWindow(blocks);
  const span = Math.max(1, endMin - startMin);
  const boxes: LayoutBox[] = [];
  blocks.forEach((b, i) => {
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return;
    const rawTop = ((s - startMin) / span) * 100;
    const rawHeight = ((e - s) / span) * 100;
    const heightPct = Math.max(minVisiblePct, rawHeight);
    const topPct = Math.max(0, Math.min(rawTop, 100 - heightPct));
    boxes.push({ blockIndex: i, startMin: s, endMin: e, topPct, heightPct });
  });
  return { windowStartMin: startMin, windowEndMin: endMin, boxes };
}

/** Ob ein Block die feste Pause ist (zählt nicht zum Budget). */
export function isPauseBlock(b: Pick<PbScheduleBlock, 'care_type'>): boolean {
  return b.care_type === PAUSE_ACTIVITY;
}

/** Summe der frei einplanbaren Tätigkeiten (ohne Pause) in Minuten. */
export function plannedActivityMinutes(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time' | 'care_type'>>,
): number {
  return blocks.filter(b => !isPauseBlock(b)).reduce((acc, b) => acc + blockMinutes(b), 0);
}

/** Summe der Pause-Blöcke in Minuten. */
export function pauseMinutes(
  blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time' | 'care_type'>>,
): number {
  return blocks.filter(isPauseBlock).reduce((acc, b) => acc + blockMinutes(b), 0);
}

/** Ob die Bis-Zeit dieser Tätigkeit automatisch gesetzt wird. */
export function hasFixedDuration(careType: string | null): boolean {
  return careType === PAUSE_ACTIVITY || careType === MEDICATION_ACTIVITY;
}

/**
 * Start-/Endzeit für eine NEUE Tätigkeit.
 */
export function nextEntryTimes(
  blocks: Array<Pick<PbScheduleBlock, 'end_time'>>,
): { start_time: string; end_time: string } {
  const lastEnd = blocks.reduce((max, b) => {
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(e)) return max;
    return Number.isNaN(max) ? e : Math.max(max, e);
  }, NaN);
  const startMin = Number.isNaN(lastEnd) ? 8 * 60 : Math.min(lastEnd, 23 * 60);
  const endMin = Math.min(startMin + 60, 23 * 60 + 59);
  return { start_time: fmtClock(startMin), end_time: fmtClock(endMin) };
}

/**
 * Auto-Bis-Zeit für Tätigkeiten mit fixer Dauer ("HH:MM") oder null.
 */
export function autoEndFor(careType: string | null, startTime: string): string | null {
  const s = timeToMinutes(startTime);
  if (Number.isNaN(s)) return null;
  if (careType === PAUSE_ACTIVITY) return fmtClock(s + FIXED_BREAK_MIN);
  if (careType === MEDICATION_ACTIVITY) return fmtClock(s + MEDICATION_DURATION_MIN);
  return null;
}

/** Standard-Dauer (Min) einer Tätigkeit aus der Zeitliste; null wenn keine. */
export function defaultDurationFor(careType: string | null): number | null {
  if (!careType) return null;
  return ACTIVITY_DEFAULT_MIN[careType] ?? null;
}

/**
 * Bis-Zeit beim Auswählen einer Tätigkeit.
 */
export function endForActivitySelection(careType: string | null, startTime: string): string | null {
  const fixed = autoEndFor(careType, startTime);
  if (fixed !== null) return fixed;
  const def = defaultDurationFor(careType);
  const s = timeToMinutes(startTime);
  if (def !== null && !Number.isNaN(s)) return fmtClock(Math.min(s + def, DAY_END_MIN));
  return null;
}

/**
 * Dauer (Min), mit der eine Tätigkeit beim Ablegen einer Kachel angelegt wird:
 * fixe Dauer (Pause/Medikamente), sonst Standard-Dauer, sonst 60 Min.
 */
export function activityDurationMin(careType: string | null): number {
  if (careType === PAUSE_ACTIVITY) return FIXED_BREAK_MIN;
  if (careType === MEDICATION_ACTIVITY) return MEDICATION_DURATION_MIN;
  return defaultDurationFor(careType) ?? FALLBACK_ACTIVITY_MIN;
}

/**
 * Start/Ende eines Eintrags, der per Kachel an Minute `startMin` abgelegt
 * wird. Die Dauer bleibt erhalten; reicht sie über das Tagesende, rückt der
 * Start nach vorn.
 */
export function dropBlockRange(careType: string | null, startMin: number): { start: number; end: number } {
  const dur = activityDurationMin(careType);
  const start = Math.max(0, Math.min(startMin, DAY_END_MIN - dur));
  return { start, end: start + dur };
}

/**
 * Minute (auf `stepMin` gerundet) aus einer relativen Position 0..1 über
 * einem Zeitraster von `windowStartMin` bis `windowEndMin`.
 */
export function minuteFromFraction(frac: number, windowStartMin: number, windowEndMin: number, stepMin = 5): number {
  const f = Math.max(0, Math.min(1, frac));
  const raw = windowStartMin + f * (windowEndMin - windowStartMin);
  const snapped = Math.round(raw / stepMin) * stepMin;
  return Math.max(windowStartMin, Math.min(windowEndMin, snapped));
}

/** Gemessene Tabellenzeile: Bildschirmlage plus Startminute des Slots. */
export interface MeasuredSlotRow { top: number; bottom: number; startMin: number }

/** Zeilen einer Tagestabelle (`tr[data-slot-min]`) messen; Zeilen ohne Höhe (jsdom, display:none) entfallen. */
export function measureSlotRows(table: HTMLElement): MeasuredSlotRow[] {
  const out: MeasuredSlotRow[] = [];
  table.querySelectorAll<HTMLElement>('tr[data-slot-min]').forEach(row => {
    const r = row.getBoundingClientRect();
    if (r.height > 0) out.push({ top: r.top, bottom: r.bottom, startMin: Number(row.dataset.slotMin) });
  });
  return out;
}

/**
 * Minute (auf `stepMin` gerundet) aus der vertikalen Position über den ECHTEN
 * Zeilen. Zeilen mit Beschreibungstext sind höher als 28 px; eine lineare
 * Rechnung über die Tabellenhöhe träfe dann die falsche Zeit. Gemeinsame Regel
 * für Slot-Auswahl und Kachel-Drop auf dem Kundenlink. `null`, wenn keine Zeile
 * gemessen werden konnte (Aufrufer fällt dann auf die lineare Rechnung zurück).
 */
export function minuteFromRows(
  clientY: number,
  rows: MeasuredSlotRow[],
  rowSpanMin = 30,
  stepMin = 15,
): number | null {
  if (rows.length === 0) return null;
  const lo = rows[0].startMin;
  const hi = rows[rows.length - 1].startMin + rowSpanMin;
  if (clientY <= rows[0].top) return lo;
  const last = rows[rows.length - 1];
  if (clientY >= last.bottom) return hi;
  const row = rows.find(r => clientY >= r.top && clientY < r.bottom) ?? last;
  const frac = Math.max(0, Math.min(1, (clientY - row.top) / (row.bottom - row.top)));
  const snapped = Math.round((row.startMin + frac * rowSpanMin) / stepMin) * stepMin;
  return Math.max(lo, Math.min(hi, snapped));
}

/**
 * Index des ersten Eintrags, an dem `validateBlocks` scheitern würde
 * (unvollständig, Ende vor Start, ohne Tätigkeit, Überschneidung) — oder -1.
 * Damit kann die Liste genau diesen Eintrag aufklappen.
 */
export function firstInvalidBlockIndex(blocks: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time' | 'care_type'>>): number {
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const s = timeToMinutes(b.start_time ?? '');
    const e = timeToMinutes(b.end_time ?? '');
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s || !b.care_type) return i;
  }
  for (let i = 0; i < blocks.length; i++) {
    const s = timeToMinutes(blocks[i].start_time);
    const e = timeToMinutes(blocks[i].end_time);
    if (rangeOverlaps(s, e, blocks, i)) return i;
  }
  return -1;
}

/**
 * Prüft, ob der Zeitraum [startMin, endMin) einen bestehenden Eintrag überschneidet.
 */
export function rangeOverlaps(
  startMin: number,
  endMin: number,
  list: Array<Pick<PbScheduleBlock, 'start_time' | 'end_time'>>,
  exceptIndex = -1,
): boolean {
  return list.some((b, i) => {
    if (i === exceptIndex) return false;
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e)) return false;
    return startMin < e && endMin > s;
  });
}

/**
 * Neuer Start/Ende eines um `deltaMin` verschobenen Blocks.
 */
export function movedRange(
  blockStart: number,
  blockEnd: number,
  deltaMin: number,
): { start: number; end: number } {
  const dur = blockEnd - blockStart;
  const start = Math.max(0, Math.min(blockStart + deltaMin, DAY_END_MIN - dur));
  return { start, end: start + dur };
}

/**
 * Start/Ende eines NEUEN Blocks aus einer Klick/Zieh-Geste im Zeitraster.
 */
export function gridDraftRange(lo: number, hi: number): { start: number; end: number } {
  if (hi <= lo) {
    const start = Math.min(lo, DAY_END_MIN - 60);
    return { start, end: start + 60 };
  }
  return { start: lo, end: Math.min(hi, DAY_END_MIN) };
}

/**
 * Validiert alle Einträge. Gibt die erste Verletzung zurück, sonst null.
 */
export function validateBlocks(blocks: PbScheduleBlock[]): ScheduleValidationError | null {
  for (const b of blocks) {
    if (!b.start_time || !b.end_time) return { code: 'INCOMPLETE' };
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e)) return { code: 'INCOMPLETE' };
    if (e <= s) return { code: 'END_BEFORE_START' };
    if (!b.care_type) return { code: 'NO_ACTIVITY' };
  }

  const ranges = blocks
    .map(b => ({ s: timeToMinutes(b.start_time), e: timeToMinutes(b.end_time) }))
    .sort((a, b) => a.s - b.s);
  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i].s < ranges[i - 1].e) return { code: 'OVERLAP' };
  }

  const planned = plannedActivityMinutes(blocks);
  if (planned > DAY_ACTIVITY_BUDGET_MIN) return { code: 'OVERLIMIT', totalMinutes: planned };

  return null;
}

/**
 * Auto-Kaskade beim Ändern einer Bis-Zeit (US-WP10 / #24, erweitert WP-Damon).
 */
export function cascadeFromEndChange<T extends { _key: string; start_time: string; end_time: string }>(
  blocks: T[],
  editedKey: string,
  newEnd: string,
): T[] {
  const newEndMin = timeToMinutes(newEnd);
  const withEnd = blocks.map(b => (b._key === editedKey ? { ...b, end_time: newEnd } : b));
  if (Number.isNaN(newEndMin)) return withEnd;

  const edited = withEnd.find(b => b._key === editedKey);
  if (!edited) return withEnd;
  const editedStart = timeToMinutes(edited.start_time);
  if (Number.isNaN(editedStart)) return withEnd;

  const order = [...withEnd]
    .filter(b => !Number.isNaN(timeToMinutes(b.start_time)))
    .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
  const editedPos = order.findIndex(b => b._key === editedKey);
  if (editedPos < 0) return withEnd;

  const following = order.slice(editedPos + 1);
  if (following.length === 0) return withEnd;

  const nextStart = timeToMinutes(following[0].start_time);
  const delta = newEndMin - nextStart;
  if (delta === 0) return withEnd;

  let shift: number;
  if (delta > 0) {
    const lastFollowingEnd = following.reduce((max, b) => {
      const e = timeToMinutes(b.end_time);
      if (Number.isNaN(e)) return max;
      return Number.isNaN(max) ? e : Math.max(max, e);
    }, NaN);
    const maxForward = Number.isNaN(lastFollowingEnd)
      ? delta
      : Math.max(0, DAY_END_MIN - lastFollowingEnd);
    shift = Math.min(delta, maxForward);
  } else {
    const earliestFollowingStart = following.reduce((min, b) => {
      const s = timeToMinutes(b.start_time);
      if (Number.isNaN(s)) return min;
      return Number.isNaN(min) ? s : Math.min(min, s);
    }, NaN);
    const maxBackward = Number.isNaN(earliestFollowingStart)
      ? -delta
      : Math.max(0, earliestFollowingStart);
    shift = -Math.min(-delta, maxBackward);
  }
  if (shift === 0) return withEnd;

  const shiftKeys = new Set(following.map(b => b._key));
  return withEnd.map(b => {
    if (!shiftKeys.has(b._key)) return b;
    const s = timeToMinutes(b.start_time);
    const e = timeToMinutes(b.end_time);
    if (Number.isNaN(s) || Number.isNaN(e)) return b;
    return { ...b, start_time: fmtClock(s + shift), end_time: fmtClock(e + shift) };
  });
}

/**
 * Eintrag in der (nach Startzeit sortierten) Liste an Position `toIndex`
 * verschieben — die Reihenfolge bestimmt die Uhrzeiten. Betroffen ist nur der
 * Bereich zwischen alter und neuer Position: Er beginnt weiter zur Startzeit
 * seines ersten Eintrags, die Einträge folgen in neuer Reihenfolge mit ihrer
 * Dauer aufeinander, die Lücken zwischen den Positionen bleiben erhalten.
 * Damit endet der Bereich zur selben Zeit wie vorher und es entsteht keine
 * Überschneidung mit den übrigen Einträgen. Einträge ohne gültige Uhrzeit im
 * Bereich → keine Änderung.
 */
export function reorderBlocks<T extends { _key: string; start_time: string; end_time: string }>(
  blocks: T[],
  key: string,
  toIndex: number,
): T[] {
  const order = [...blocks].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
  const from = order.findIndex(b => b._key === key);
  if (from < 0) return blocks;
  const to = Math.max(0, Math.min(toIndex, order.length - 1));
  if (from === to) return blocks;
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const range = order.slice(lo, hi + 1);
  const times = range.map(b => ({ s: timeToMinutes(b.start_time), e: timeToMinutes(b.end_time) }));
  if (times.some(t => Number.isNaN(t.s) || Number.isNaN(t.e) || t.e < t.s)) return blocks;
  const gaps = times.slice(1).map((t, i) => Math.max(0, t.s - times[i].e));

  const moved = range[from - lo];
  const rest = range.filter(b => b._key !== key);
  const newRange = from < to ? [...rest, moved] : [moved, ...rest];

  const patch = new Map<string, { start_time: string; end_time: string }>();
  let cur = times[0].s;
  newRange.forEach((b, i) => {
    const dur = blockMinutes(b);
    patch.set(b._key, { start_time: fmtClock(cur), end_time: fmtClock(cur + dur) });
    cur += dur + (gaps[i] ?? 0);
  });
  return blocks.map(b => {
    const p = patch.get(b._key);
    return p ? { ...b, ...p } : b;
  });
}
